/**
 * Claves de API para el TPV y el ERP.
 *
 * La clave en claro existe UNA vez: en la respuesta del alta. Ni el servidor
 * la puede recuperar (guarda el SHA-256). De ahí el aviso amarillo y el botón
 * de copiar: si el admin cierra esto sin copiarla, toca revocar y generar otra.
 */
import { useCallback, useEffect, useState } from 'react';
import { AdminLayout } from './AdminLayout';
import { getApiKeys, createApiKey, revokeApiKey, apiMessage, ApiKeyRow } from '../../services/apiClient';
import { Icon } from '../ui/Icon';

export function ApiKeysAdmin() {
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [fresh, setFresh] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setKeys(await getApiKeys());
  }, []);

  useEffect(() => {
    load().catch(() => setError('No se pudieron cargar las claves'));
  }, [load]);

  const generate = async () => {
    setError(null);
    try {
      const res = await createApiKey(name.trim());
      setFresh(res.api_key);
      setCopied(false);
      setName('');
      await load();
    } catch {
      setError('No se pudo generar la clave');
    }
  };

  const copy = async () => {
    if (!fresh) return;
    try {
      await navigator.clipboard.writeText(fresh);
      setCopied(true);
    } catch {
      // Sin permiso de portapapeles: la clave sigue visible para copiar a mano.
    }
  };

  // Revocar es irreversible (el claro no existe y las señales de ese TPV
  // dejan de entrar): mismo diálogo de confirmación que las acciones
  // destructivas vecinas (WorksitesAdmin, ResourcePlanner). Y sin
  // try/catch, un JWT caducado dejaba el rechazo sin manejar: ningún error
  // en pantalla y la clave sospechosa seguía activa con aspecto de revocada.
  const revoke = async (id: number, name: string) => {
    if (!confirm(`¿Revocar la clave «${name}»? Quien la use deja de entrar al instante y no se puede deshacer.`)) return;
    setError(null);
    try {
      await revokeApiKey(id);
      await load();
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo revocar la clave: sigue activa, inténtalo de nuevo'));
    }
  };

  return (
    <AdminLayout title="Claves de API" subtitle="Para que el TPV y el ERP corroboren la actividad">
      <div className="max-w-[800px] space-y-4">
        {error && <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14px]">{error}</p>}

        <div className="card-surface p-5 space-y-3.5">
          <div className="flex flex-wrap items-end gap-3">
            <label className="block flex-1 min-w-[200px]">
              <span className="label">Nombre (qué app la usa)</span>
              <input
                className="field"
                placeholder="TPV Restaurante Centro"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <button
              onClick={generate}
              disabled={!name.trim()}
              className="h-12 px-5 rounded-xl bg-brand text-on-brand font-bold text-[14px] disabled:opacity-40"
            >
              Generar clave
            </button>
          </div>

          {fresh && (
            <div className="rounded-xl bg-warn-soft border border-warn p-4 animate-rise">
              <p className="flex items-center gap-2 font-semibold text-[14px] text-warn mb-2.5">
                <Icon name="alert" size={18} strokeWidth={1.8} />
                Cópiala ahora. No volverá a mostrarse.
              </p>
              <div className="flex gap-2 items-stretch">
                <code className="flex-1 bg-canvas border border-line-strong rounded-lg px-3.5 py-3 font-mono text-[13px] overflow-x-auto whitespace-nowrap">
                  {fresh}
                </code>
                <button
                  onClick={copy}
                  className="px-4 rounded-lg bg-ink text-canvas font-semibold text-[13px] shrink-0"
                >
                  {copied ? 'Copiada ✓' : 'Copiar'}
                </button>
              </div>
              <button onClick={() => setFresh(null)} className="mt-2.5 text-[13px] text-ink-2">
                Ya la he guardado — ocultar
              </button>
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-surface border border-line overflow-hidden">
          {keys.map((k) => (
            <div key={k.id} className="flex items-center gap-3.5 px-5 py-3.5 border-b border-line last:border-b-0">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[14.5px] truncate">{k.name}</p>
                <p className="font-mono text-[13px] text-ink-3">{k.masked}</p>
              </div>
              <span className="font-mono text-[11.5px] text-ink-2 shrink-0 hidden sm:block">
                {k.last_used_at
                  ? `usada ${new Date(k.last_used_at).toLocaleDateString('es-ES')}`
                  : `creada ${new Date(k.created_at).toLocaleDateString('es-ES')}`}
              </span>
              <button
                onClick={() => revoke(k.id, k.name)}
                className="h-[34px] px-3 rounded-lg border border-line-strong text-danger font-semibold text-[12.5px] shrink-0"
              >
                Revocar
              </button>
            </div>
          ))}
          {keys.length === 0 && (
            <p className="text-center text-[14px] text-ink-3 py-10">
              Sin claves. Genera una para conectar el TPV o el ERP.
            </p>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
